import { createContext, useState, useEffect, useContext, ReactNode } from 'react';

// Since this is a Spanish-only website, we'll simplify the language context
// but keep the structure for potential future localization
type Language = 'es';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

// Simple translations (only Spanish for now)
const translations: Record<Language, Record<string, string>> = {
  es: {
    'home': 'Inicio',
    'services': 'Servicios',
    'courses': 'Cursos',
    'blog': 'Blog',
    'about': 'Sobre mí',
    'contact': 'Contacto',
    'footer_rights': 'Todos los derechos reservados',
    'more_info': 'Más información',
    'learn_more': 'Saber más',
    'contact_now': 'Contactar ahora',
    'send_message': 'Enviar mensaje',
    'name': 'Nombre',
    'email': 'Correo electrónico',
    'phone': 'Teléfono',
    'subject': 'Asunto',
    'message': 'Mensaje',
    'privacy_policy': 'Política de privacidad',
    'accept_privacy': 'Acepto la política de privacidad',
    'send': 'Enviar',
    'required_field': 'Campo requerido',
    // Add more translations as needed
  }
};

const initialState: LanguageContextType = {
  language: 'es',
  setLanguage: () => null,
  t: (key: string) => key,
};

const LanguageContext = createContext<LanguageContextType>(initialState);

export function LanguageProvider({ children }: { children: ReactNode }) {
  // We always use Spanish, but keep the state for future localization potential
  const [language, setLanguage] = useState<Language>('es');

  useEffect(() => {
    // Since we're Spanish-only, ensure the HTML lang attribute is set to Spanish
    document.documentElement.lang = 'es';
  }, []);

  // Translation function
  const t = (key: string): string => {
    if (translations.es && translations.es[key]) {
      return translations.es[key];
    }
    return key; // Fallback to the key itself if translation not found
  };

  const value = {
    language,
    setLanguage,
    t,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;